import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Upload,
  Edit2,
  FileText,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  Loader2,
  Eye,
} from "lucide-react";
import { motion } from "framer-motion";
import { fetchUserRoutes } from "@/store/slices/routesSlice";
import Card from "../../common/Card";
import Button from "../../common/Button";
import Table from "../../common/Table";
import RouteFormModal from "../RouteFormModal";
import RouteViewModal from "../RouteViewModal";
import RouteEditModal from "../RouteEditModal";

const Contribute = () => {
  const dispatch = useDispatch();
  const { userRoutes, isLoading } = useSelector((state) => state.routes);
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Fetch user routes on mount
  useEffect(() => {
    dispatch(fetchUserRoutes());
  }, [dispatch]);

  const handleViewRoute = (route) => {
    setSelectedRoute(route);
    setViewModalOpen(true);
  };

  const handleEditRoute = (route) => {
    setSelectedRoute(route);
    setEditModalOpen(true);
  };

  const columns = [
    {
      key: "name",
      title: "Route Name",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-green-600 dark:text-green-400">
            <FileText size={18} />
          </div>
          <div>
            <p className="font-medium text-gray-800 dark:text-gray-200">
              {row.name}
            </p>
            <p className="text-xs text-gray-500">
              {row.distance?.toFixed(1) || "0.0"} km • {row.difficulty}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "date",
      title: "Date Uploaded",
      render: (row) => (
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
          <Calendar size={16} />
          {new Date(row.createdAt).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </div>
      ),
    },
    {
      key: "status",
      title: "Status",
      render: (row) => {
        let statusColor = "bg-gray-100 text-gray-600";
        let StatusIcon = Clock;
        let statusText = row.status;

        if (row.status === "approved") {
          statusColor =
            "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
          StatusIcon = CheckCircle;
          statusText = "Approved";
        } else if (row.status === "rejected") {
          statusColor =
            "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
          StatusIcon = XCircle;
          statusText = "Rejected";
        } else if (row.status === "pending") {
          statusColor =
            "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
          StatusIcon = Clock;
          statusText = "Pending";
        }

        return (
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusColor}`}
          >
            <StatusIcon size={14} />
            {statusText}
          </span>
        );
      },
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => {
        // Only pending routes can be edited
        if (row.status === "pending") {
          return (
            <button
              onClick={() => handleEditRoute(row)}
              className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
            >
              <Edit2 size={14} />
              Edit
            </button>
          );
        }
        // All other statuses (approved, rejected, hidden) get view button
        return (
          <button
            onClick={() => handleViewRoute(row)}
            className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
          >
            <Eye size={14} />
            View
          </button>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <Loader2 className="animate-spin w-6 h-6 mb-2" />
        <p>Loading your contributions...</p>
      </div>
    );
  }

  return (
    <section className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            Contribute
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Help expand the Koset cycling network by uploading your routes.
          </p>
        </div>
        <Button
          onClick={() => setModalOpen(true)}
          className="flex items-center gap-2"
        >
          <Upload className="w-5 h-5" />
          Upload New Route
        </Button>
      </div>

      {/* Contributions Table */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 px-1">
          Your Contributions
        </h3>

        {userRoutes.length === 0 ? (
          <Card className="p-12 text-center bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-2xl">
            <div className="max-w-md mx-auto">
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Upload className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                No routes yet
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Be the first to contribute by uploading your favorite cycling
                routes to help the community.
              </p>
              <Button onClick={() => setModalOpen(true)}>
                Upload Your First Route
              </Button>
            </div>
          </Card>
        ) : (
          <Table
            columns={columns}
            data={userRoutes}
            renderRow={(row) => (
              <tr
                key={row.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
              >
                {columns.map((col) => (
                  <td
                    key={`${row.id}-${col.key}`}
                    className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300"
                  >
                    {col.render ? col.render(row) : row[col.key]}
                  </td>
                ))}
              </tr>
            )}
          />
        )}
      </div>

      {/* Route Form Modal */}
      <RouteFormModal open={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Route View Modal */}
      <RouteViewModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedRoute(null);
        }}
        route={selectedRoute}
      />

      {/* Route Edit Modal */}
      <RouteEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedRoute(null);
        }}
        route={selectedRoute}
      />
    </section>
  );
};

export default Contribute;
